import { endsAtBat } from "../constants/tagOptions";
import {
  Activity,
  GameFullGame,
  GameFullResponse,
  GamePlayerRef,
  PlateAppearance,
  ResultingState,
  Sport,
} from "../types/gameFull";
import { getSuggestedContactType, getSuggestedPitchType } from "./pitchTagging";
import { comparePlateAppearances } from "./plateAppearanceId";

export type RunsOuts = { runs: number; outs: number };

/** What a pitch row shows, by whether it is tagged and how it ended. */
export type PitchSummary =
  /** Untagged and hit: the tracker's contact type and exit velo */
  | {
      kind: "untaggedHit";
      contactType: string | null;
      exitVelocity: number;
    }
  /** Untagged and not hit: the classifier's pitch type and velo */
  | {
      kind: "untaggedPitch";
      pitchType: string | null;
      pitchVelocity: number | null;
    }
  | {
      kind: "inPlay";
      playResult: string;
      contactType: string | null;
      exitVelocity: number | null;
      runsOuts: RunsOuts | null;
    }
  /** Not in play (fouls included): the count, or runs/outs if it ended the PA */
  | {
      kind: "pitch";
      result: string;
      pitchType: string | null;
      pitchVelocity: number | null;
      /** Count after the pitch ("1-0"), for results that don't end the at bat */
      count: string | null;
      runsOuts: RunsOuts | null;
    };

export type FeedPitch = {
  activityId: string;
  /** The pitch's omnibus event uuid, for tests to find the row by */
  omnibusEventId: string;
  pitchNumber: number;
  isTagged: boolean;
  summary: PitchSummary;
};

export type HalfInningGroup = {
  key: string;
  inning: number;
  isTopOfInning: boolean;
  plateAppearances: PlateAppearance[];
};

// Seam-count names are baseball's; softball shows the plain pitch name
const BASEBALL_PITCH_TYPE_LABELS: Record<string, string> = {
  Fastball: "4S Fastball",
  TwoSeamFastball: "2S Fastball",
};

const RESULT_LABELS: Record<string, string> = {
  CalledStrike: "Called Strike",
  StrikeCalled: "Called Strike",
  SwingingStrike: "Swinging Strike",
  StrikeSwinging: "Swinging Strike",
  BallCalled: "Ball",
  NotAPitch: "Ignore",
  HitByPitch: "Hit By Pitch",
  CatchersInt: "Catcher's Interference",
  FieldersChoice: "Fielder's Choice",
  Popup: "Popup",
};

/** "StrikeoutSwinging" -> "Strikeout Swinging" */
const humanize = (value: string) =>
  RESULT_LABELS[value] ?? value.replace(/([a-z])([A-Z])/g, "$1 $2");

export const ordinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

export const formatHalfInning = (inning: number, isTopOfInning: boolean) =>
  `${isTopOfInning ? "Top" : "Bottom"} of ${ordinal(inning)}`;

export const formatPitchType = (
  pitchType: string | null | undefined,
  sport: Sport,
) => {
  if (!pitchType) return null;
  const label =
    sport === "baseball" ? BASEBALL_PITCH_TYPE_LABELS[pitchType] : undefined;
  return label ?? humanize(pitchType);
};

/** Full name of a tag value, e.g. "StrikeoutLooking" -> "Strikeout Looking" */
export const formatTagValue = (
  tag: "pitchType" | "pitchResult" | "contactType" | "playResult",
  value: string | null,
  sport: Sport,
) => {
  if (!value) return null;
  return tag === "pitchType" ? formatPitchType(value, sport) : humanize(value);
};

const formatContactType = (contactType: string | null | undefined) =>
  contactType ? humanize(contactType) : null;

/** One decimal, without a trailing ".0": 85.8 -> "85.8", 93.04 -> "93" */
export const formatMph = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Right side of a row that ended the at bat. Both at once only fit as short
 * lines ("1 out", "1 run").
 */
export const formatRunsOuts = ({ runs, outs }: RunsOuts): string[] | null => {
  if (runs > 0 && outs > 0) return [plural(outs, "out"), plural(runs, "run")];
  if (runs > 0) return [`${plural(runs, "run")} scored`];
  if (outs > 0) return [`${plural(outs, "out")} recorded`];
  return null;
};

const getRunsOuts = ({ resultingState }: Activity): RunsOuts | null =>
  resultingState
    ? { runs: resultingState.runsScored, outs: resultingState.outsRecorded }
    : null;

export const getPitchSummary = (
  activity: Activity,
  sport: Sport,
): PitchSummary => {
  const { result, pitch } = activity;
  const pitchVelocity = pitch.pitchData?.RelSpeedMPH ?? null;
  const exitVelocity = pitch.hitData?.ExitSpeedMPH ?? null;

  if (!result) {
    return exitVelocity != null
      ? {
          kind: "untaggedHit",
          contactType: formatContactType(
            getSuggestedContactType(pitch.hitData),
          ),
          exitVelocity,
        }
      : {
          kind: "untaggedPitch",
          pitchType: formatPitchType(
            getSuggestedPitchType(pitch.pitchData, sport),
            sport,
          ),
          pitchVelocity,
        };
  }

  if (result.pitchResult === "InPlay") {
    return {
      kind: "inPlay",
      playResult: result.playResult ? humanize(result.playResult) : "In Play",
      contactType: formatContactType(result.contactType),
      exitVelocity,
      runsOuts: getRunsOuts(activity),
    };
  }

  const resultValue = result.playResult ?? result.pitchResult;
  return {
    kind: "pitch",
    result: resultValue ? humanize(resultValue) : "Unknown Result",
    pitchType: formatPitchType(result.pitchType, sport),
    pitchVelocity,
    count: formatCountAfter(activity),
    runsOuts: endsAtBat(result.pitchResult, result.playResult)
      ? getRunsOuts(activity)
      : null,
  };
};

/**
 * Count after the pitch, straight from the game service's resulting state.
 * Left off when the pitch ended the at bat or moved no count.
 */
const formatCountAfter = ({ result, resultingState }: Activity) => {
  if (!result?.pitchResult || !resultingState) return null;
  if (endsAtBat(result.pitchResult, result.playResult)) return null;
  if (result.pitchResult === "InPlay" || result.pitchResult === "NotAPitch") {
    return null;
  }
  return `${resultingState.balls}-${resultingState.strikes}`;
};

export type PitchMatchup = {
  hitter: GamePlayerRef | null;
  pitcher: GamePlayerRef | null;
  isTopOfInning: boolean;
};

/**
 * Who faced the pitch: its plate appearance's players, or the game's current
 * ones while waiting on the next pitch.
 */
export const getPitchMatchup = (
  data: GameFullResponse,
  activity: Activity | null,
): PitchMatchup => {
  const pa =
    activity &&
    data.plateAppearances.find(
      (p) => p.plateAppearanceId === activity.plateAppearanceId,
    );
  if (pa) {
    return {
      hitter: pa.hitter,
      pitcher: pa.pitcher,
      isTopOfInning: pa.isTopOfInning,
    };
  }
  return {
    hitter: data.game.currentHitter,
    pitcher: data.game.currentPitcher,
    isTopOfInning: data.game.isTopOfInning,
  };
};

export const getActivePlateAppearance = (data: GameFullResponse) =>
  data.plateAppearances.find(
    (pa) => pa.plateAppearanceId === data.game.currentPlateAppearanceId,
  ) ?? null;

/** Activities for a PA in chronological order (first pitch first). */
export const getActivitiesForPA = (
  data: GameFullResponse,
  plateAppearanceId: string,
) =>
  data.activities
    .filter((activity) => activity.plateAppearanceId === plateAppearanceId)
    .sort((a, b) => a.pitch.eventTime - b.pitch.eventTime);

/**
 * The PA's last tagged pitch, the one Post At-Bat Update re-tags with its
 * edits. Not a Pitch tags are skipped: they carry no results or players.
 */
export const getLastTaggedPitch = (
  data: GameFullResponse,
  plateAppearanceId: string,
) =>
  getActivitiesForPA(data, plateAppearanceId)
    .filter(
      (activity) =>
        activity.result?.pitchResult &&
        activity.result.pitchResult !== "NotAPitch",
    )
    .at(-1);

/** The most recently thrown pitch in the game. */
export const getNewestActivity = (activities: Activity[]) =>
  activities.reduce<Activity | undefined>(
    (latest, activity) =>
      !latest || activity.pitch.eventTime > latest.pitch.eventTime
        ? activity
        : latest,
    undefined,
  );

/** Pitches for a PA, numbered chronologically and returned newest first. */
export const getPitchesForPA = (
  data: GameFullResponse,
  plateAppearanceId: string,
): FeedPitch[] => {
  const pitches = getActivitiesForPA(data, plateAppearanceId).map(
    (activity, index): FeedPitch => ({
      activityId: activity.activityId,
      omnibusEventId: activity.pitch.omnibusEventId,
      pitchNumber: index + 1,
      isTagged: Boolean(activity.result),
      summary: getPitchSummary(activity, data.game.sport),
    }),
  );

  return pitches.reverse();
};

/** Game state after the PA, taken from its latest activity that has one. */
export const getResultingState = (
  data: GameFullResponse,
  plateAppearanceId: string,
): ResultingState | null => {
  const activities = getActivitiesForPA(data, plateAppearanceId);
  for (let i = activities.length - 1; i >= 0; i--) {
    const state = activities[i].resultingState;
    if (state) return state;
  }
  return null;
};

const halfInningKey = (inning: number, isTopOfInning: boolean) =>
  `${inning}-${isTopOfInning ? "top" : "bottom"}`;

/**
 * Half-innings the game history opens by default: the one being played and
 * the one before it. Older ones start closed, so a long game doesn't render
 * every pitch it has had.
 */
export const getRecentHalfInningKeys = ({
  inning,
  isTopOfInning,
}: Pick<GameFullGame, "inning" | "isTopOfInning">) =>
  new Set([
    halfInningKey(inning, isTopOfInning),
    isTopOfInning
      ? halfInningKey(inning - 1, false)
      : halfInningKey(inning, true),
  ]);

/**
 * Whether a PA belongs in the game history: it ended, or it was left without
 * ending. Edit Live Game State moving the game to another half-inning leaves
 * the open PA "started" but no longer current (win-game-service
 * StateTaggedHandler), and its pitches would show nowhere. A pending PA the
 * page seated itself is merged into the current one instead, so it's left out.
 */
const isHistoryPlateAppearance = (
  data: GameFullResponse,
  pa: PlateAppearance,
) =>
  pa.status === "ended" ||
  (pa.plateAppearanceId !== data.game.currentPlateAppearanceId &&
    !pa.isPending &&
    getActivitiesForPA(data, pa.plateAppearanceId).length > 0);

/** Past PAs grouped by half-inning, newest half-inning and PA first. */
export const groupHistoryByHalfInning = (
  data: GameFullResponse,
): HalfInningGroup[] => {
  const ended = data.plateAppearances
    .filter((pa) => isHistoryPlateAppearance(data, pa))
    .sort((a, b) => comparePlateAppearances(b, a));

  const groups = new Map<string, HalfInningGroup>();
  for (const pa of ended) {
    const key = halfInningKey(pa.inning, pa.isTopOfInning);
    const group = groups.get(key) ?? {
      key,
      inning: pa.inning,
      isTopOfInning: pa.isTopOfInning,
      plateAppearances: [],
    };
    group.plateAppearances.push(pa);
    groups.set(key, group);
  }

  return [...groups.values()].sort(
    (a, b) =>
      b.inning - a.inning || Number(a.isTopOfInning) - Number(b.isTopOfInning),
  );
};

/** Newest activity in the active PA, used as the default selected pitch. */
const getLatestActiveActivityId = (data: GameFullResponse) =>
  data.game.currentPlateAppearanceId
    ? getActivitiesForPA(data, data.game.currentPlateAppearanceId).at(-1)
        ?.activityId
    : undefined;

/**
 * Whether saving tags for this pitch updates the live game state (spec 3.4):
 * it must be in the active PA, with no later pitch already tagged.
 */
export const doesTagUpdateGameState = (
  data: GameFullResponse,
  activityId: string,
) => {
  const activity = data.activities.find((a) => a.activityId === activityId);
  if (
    !activity ||
    activity.plateAppearanceId !== data.game.currentPlateAppearanceId
  ) {
    return false;
  }
  return !data.activities.some(
    (a) => a.result && a.pitch.eventTime > activity.pitch.eventTime,
  );
};

/** Selection id for the "Waiting on pitch..." placeholder row. */
export const WAITING_PITCH_ID = "waiting-on-pitch";

export type WaitingPitch = {
  plateAppearanceId: string;
  pitchNumber: number;
};

/**
 * Placeholder for the next pitch of the active at bat: shown once its latest
 * pitch is tagged (or before its first pitch arrives).
 */
export const getWaitingPitch = (
  data: GameFullResponse,
): WaitingPitch | null => {
  const pa = getActivePlateAppearance(data);
  if (!pa) return null;

  const activities = getActivitiesForPA(data, pa.plateAppearanceId);
  const latest = activities.at(-1);
  if (latest && !latest.result) return null;

  return {
    plateAppearanceId: pa.plateAppearanceId,
    pitchNumber: activities.length + 1,
  };
};

/** Waiting row when there is one, else the newest pitch of the active PA. */
export const getDefaultSelectionId = (data: GameFullResponse) =>
  getWaitingPitch(data) ? WAITING_PITCH_ID : getLatestActiveActivityId(data);
